import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Form,
    Input,
    Radio,
    Row,
    Select,
    Space,
    Tooltip,
    Typography,
    UploadFile,
} from 'antd';
import {
    MessageOutlined,
    InboxOutlined,
    ArrowLeftOutlined,
    SaveOutlined,
    TeamOutlined,
    PictureOutlined,
    FileTextOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import Dragger from 'antd/es/upload/Dragger';
import { UploadChangeParam } from 'antd/es/upload';
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import ImgCrop from 'antd-img-crop';

import { IMessage } from '../../../../interfaces';
import useMessageForm from './useMessageForm';
import { removeAcento } from '../../../../utils/RemoveAcentos';
import { formatCpf } from '../../../../utils/Masks';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import AppUsersModal from './AppUsersModal';
import PhoneMockup from './PhoneMockup';
import PageHeader from '../../../../components/PageHeader';

const { Text } = Typography;

interface IProps {
    action: 'Adicionar' | 'Editar';
    message?: IMessage;
    onSubmit: (formData: FormData) => Promise<void>;
}

export default function MessageForm({ action, message, onSubmit }: IProps) {
    const navigate = useNavigate();
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        isSubmiting,
        isLoadingAppUsers,
        handleSubmit,
        handleSubmitForm,
        dummyRequest,
        appUsers,
        control,
        errors,
        defaultEditorState,
        editorState,
        handleChangeImage,
        handleRemoveImage,
        imageUrl,
        onChangeEditor,
        onChangeIsDirectMessage,
        isGeneral,
        isDirectMessage,
        isEditing,
        handleCloseAppUsersModal,
        handleOpenAppUsersModal,
        isViewAppUsersModalVisible,
        acceptedImageTypes,
        body,
        title,
        content,
        loggedInUserName,
        hasAccessManyCities,
    } = useMessageForm(onSubmit, message);

    const cardBg = isDark ? '#141414' : '#ffffff';
    const cardBorder = isDark ? '#262626' : '#e2e8f0';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* External Breadcrumb Card */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: (
                                <Link to={`/mensagens${isGeneral ? '?type=general' : ''}`}>
                                    Mensagens{isGeneral ? ' Gerais' : ''}
                                </Link>
                            ),
                        },
                        {
                            title: `${action === 'Adicionar' ? 'Nova' : 'Editar'} Mensagem${isGeneral ? ' Geral' : ''}`,
                        },
                    ]}
                />
            </div>

            <PageHeader
                icon={<MessageOutlined />}
                iconBg={
                    isGeneral
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'
                        : 'linear-gradient(135deg, #1677ff 0%, #0958d9 100%)'
                }
                title={`${action === 'Adicionar' ? 'Nova' : 'Editar'} Mensagem ${isGeneral ? 'Geral' : ''}`}
                subtitle="Preencha os campos para configurar a mensagem e acompanhe a prévia em tempo real na tela do aplicativo."
                actions={
                    <Space size={10}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(`/mensagens${isGeneral ? '?type=general' : ''}`)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 500,
                                height: 38,
                                borderRadius: 8,
                            }}
                        >
                            Voltar
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            htmlType="submit"
                            form="message-form"
                            loading={isSubmiting}
                            disabled={isSubmiting || isLoadingAppUsers}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                                height: 38,
                                borderRadius: 8,
                                backgroundColor: isGeneral ? '#8b5cf6' : '#1677ff',
                                borderColor: isGeneral ? '#8b5cf6' : '#1677ff',
                            }}
                        >
                            Salvar Mensagem
                        </Button>
                    </Space>
                }
            />

            <Form
                id="message-form"
                onFinish={handleSubmit(handleSubmitForm)}
                autoComplete="off"
                layout="vertical"
            >
                <Row gutter={[20, 20]}>
                    {/* Left Column: Form Inputs */}
                    <Col xs={24} lg={13} xl={14} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Basic Info Card */}
                        <Card
                            title={
                                <Space size={8}>
                                    <FileTextOutlined style={{ color: '#1677ff' }} />
                                    <span>Informações da Mensagem</span>
                                </Space>
                            }
                            style={{
                                background: cardBg,
                                borderColor: cardBorder,
                                borderRadius: 12,
                            }}
                            headStyle={{
                                borderBottom: `1px solid ${cardBorder}`,
                                fontWeight: 600,
                            }}
                        >
                            <Form.Item
                                label={
                                    <Space size={4}>
                                        <span style={{ fontWeight: 600 }}>Título da Mensagem</span>
                                    </Space>
                                }
                                required
                                validateStatus={errors.title ? 'error' : 'success'}
                                help={errors.title?.message}
                                style={{ marginBottom: 18 }}
                            >
                                <Controller
                                    name="title"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: 'Por favor, digite o título da Mensagem!',
                                        },
                                        maxLength: {
                                            value: 100,
                                            message: 'O título precisa ter no máximo 100 caracteres',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Ex: Novo sorteio disponível no aplicativo!"
                                            showCount
                                            maxLength={100}
                                            size="large"
                                            style={{ borderRadius: 8 }}
                                        />
                                    )}
                                />
                            </Form.Item>

                            <Form.Item
                                label={
                                    <Space size={4}>
                                        <span style={{ fontWeight: 600 }}>Conteúdo da Notificação Push</span>
                                        <Tooltip title="Este texto curto será exibido na notificação push do celular do cidadão.">
                                            <InfoCircleOutlined style={{ color: '#94a3b8' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                required
                                validateStatus={errors.content ? 'error' : 'success'}
                                help={errors.content?.message}
                                style={{ marginBottom: 0 }}
                            >
                                <Controller
                                    name="content"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: 'Por favor, digite o conteúdo da notificação!',
                                        },
                                        maxLength: {
                                            value: 255,
                                            message: 'O conteúdo precisa ter no máximo 255 caracteres',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <Input.TextArea
                                            {...field}
                                            placeholder="Ex: Acesse o app e confira os novos bilhetes da sorte gerados para suas notas fiscais..."
                                            showCount
                                            maxLength={255}
                                            rows={3}
                                            style={{ borderRadius: 8 }}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Card>

                        {/* Audience / Recipients Card */}
                        <Card
                            title={
                                <Space size={8}>
                                    <TeamOutlined style={{ color: '#1677ff' }} />
                                    <span>Destinatários e Alcance</span>
                                </Space>
                            }
                            style={{
                                background: cardBg,
                                borderColor: cardBorder,
                                borderRadius: 12,
                            }}
                            headStyle={{
                                borderBottom: `1px solid ${cardBorder}`,
                                fontWeight: 600,
                            }}
                        >
                            {isGeneral && isEditing.current && (
                                <div>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 10, fontSize: 13 }}>
                                        Esta é uma mensagem geral enviada a todos os cidadãos da plataforma.
                                    </Text>
                                    <Button
                                        onClick={handleOpenAppUsersModal}
                                        icon={<TeamOutlined />}
                                        style={{ borderRadius: 6 }}
                                    >
                                        Visualizar Leituras dos Usuários
                                    </Button>
                                </div>
                            )}

                            {!isGeneral && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    <Form.Item
                                        label={<span style={{ fontWeight: 600 }}>Tipo de Envio</span>}
                                        required
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Radio.Group
                                            value={isDirectMessage}
                                            onChange={(e) => onChangeIsDirectMessage(e.target.value)}
                                            buttonStyle="solid"
                                        >
                                            {hasAccessManyCities && (
                                                <Radio.Button
                                                    value={false}
                                                    disabled={isEditing.current && isDirectMessage}
                                                >
                                                    Mensagem Geral do Município
                                                </Radio.Button>
                                            )}
                                            <Radio.Button
                                                value={true}
                                                disabled={isEditing.current && !isDirectMessage}
                                            >
                                                Mensagem Direta (Usuários Específicos)
                                            </Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>

                                    {isEditing.current && (
                                        <div>
                                            <Button
                                                onClick={handleOpenAppUsersModal}
                                                icon={<TeamOutlined />}
                                                style={{ borderRadius: 6 }}
                                            >
                                                Visualizar Status dos Usuários
                                            </Button>
                                        </div>
                                    )}

                                    {isDirectMessage && !isEditing.current && (
                                        <Form.Item
                                            label={<span style={{ fontWeight: 600 }}>Selecionar Usuários Destinatários</span>}
                                            validateStatus={errors.app_user_ids ? 'error' : 'success'}
                                            help={errors.app_user_ids?.message}
                                            required
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Controller
                                                name="app_user_ids"
                                                control={control}
                                                rules={{
                                                    required: {
                                                        value: true,
                                                        message: 'Por favor, selecione pelo menos um usuário!',
                                                    },
                                                }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        mode="multiple"
                                                        allowClear
                                                        showSearch
                                                        size="large"
                                                        filterOption={(search, option) =>
                                                            removeAcento(option?.label || '').toLowerCase().includes(removeAcento(search).toLowerCase())
                                                        }
                                                        options={appUsers.map((appUser) => ({
                                                            value: appUser.id,
                                                            label: `${formatCpf(appUser.cpf)} • ${appUser.name}`,
                                                        }))}
                                                        placeholder="Selecione os cidadãos que receberão a mensagem..."
                                                        disabled={isLoadingAppUsers}
                                                        loading={isLoadingAppUsers}
                                                        style={{ width: '100%', borderRadius: 8 }}
                                                    />
                                                )}
                                            />
                                        </Form.Item>
                                    )}
                                </div>
                            )}
                        </Card>

                        {/* Image Upload Card */}
                        <Card
                            title={
                                <Space size={8}>
                                    <PictureOutlined style={{ color: '#1677ff' }} />
                                    <span>Imagem do Comunicado (Opcional)</span>
                                </Space>
                            }
                            style={{
                                background: cardBg,
                                borderColor: cardBorder,
                                borderRadius: 12,
                            }}
                            headStyle={{
                                borderBottom: `1px solid ${cardBorder}`,
                                fontWeight: 600,
                            }}
                        >
                            <Form.Item
                                validateStatus={errors.image ? 'error' : 'success'}
                                help={errors.image?.message}
                                style={{ marginBottom: 0 }}
                            >
                                <Controller
                                    name="image"
                                    control={control}
                                    render={({ field: { value, onChange, ...field } }) => (
                                        <div>
                                            {imageUrl && typeof imageUrl === 'string' ? (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 12,
                                                        padding: 16,
                                                        borderRadius: 8,
                                                        backgroundColor: isDark ? '#1f1f1f' : '#f8fafc',
                                                        border: `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                                                    }}
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt="Anexo da mensagem"
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: 220,
                                                            borderRadius: 8,
                                                            objectFit: 'contain',
                                                        }}
                                                    />
                                                    <Space size={10}>
                                                        <ImgCrop
                                                            modalOk="Concluído"
                                                            modalCancel="Cancelar"
                                                            modalTitle="Ajustar Imagem"
                                                            aspect={1.91 / 1}
                                                        >
                                                            <Dragger
                                                                {...field}
                                                                showUploadList={false}
                                                                customRequest={dummyRequest}
                                                                onChange={(e: UploadChangeParam<UploadFile>) =>
                                                                    handleChangeImage({
                                                                        onChangeHookForm: onChange,
                                                                        file: e.file,
                                                                    })
                                                                }
                                                                accept={acceptedImageTypes.toString()}
                                                                style={{ padding: 0, border: 'none', background: 'transparent' }}
                                                            >
                                                                <Button size="small" style={{ borderRadius: 6 }}>
                                                                    Trocar Imagem
                                                                </Button>
                                                            </Dragger>
                                                        </ImgCrop>
                                                        <Button
                                                            danger
                                                            size="small"
                                                            icon={<DeleteOutlined />}
                                                            onClick={handleRemoveImage}
                                                            style={{ borderRadius: 6 }}
                                                        >
                                                            Remover Imagem
                                                        </Button>
                                                    </Space>
                                                </div>
                                            ) : (
                                                <ImgCrop
                                                    modalOk="Concluído"
                                                    modalCancel="Cancelar"
                                                    modalTitle="Editar Enquadramento"
                                                    aspect={1.91 / 1}
                                                >
                                                    <Dragger
                                                        {...field}
                                                        showUploadList={false}
                                                        customRequest={dummyRequest}
                                                        onChange={(e: UploadChangeParam<UploadFile>) =>
                                                            handleChangeImage({
                                                                onChangeHookForm: onChange,
                                                                file: e.file,
                                                            })
                                                        }
                                                        accept={acceptedImageTypes.toString()}
                                                        style={{
                                                            borderRadius: 10,
                                                            padding: '24px 16px',
                                                            background: isDark ? '#141414' : '#fafafa',
                                                            borderColor: isDark ? '#303030' : '#d9d9d9',
                                                        }}
                                                    >
                                                        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                            <InboxOutlined style={{ color: '#1677ff', fontSize: 36 }} />
                                                        </p>
                                                        <p className="ant-upload-text" style={{ fontWeight: 600, fontSize: 14 }}>
                                                            Clique ou arraste o arquivo de imagem para esta área
                                                        </p>
                                                        <p className="ant-upload-hint" style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#8c8c8c' }}>
                                                            Formatos aceitos: PNG, JPG, JPEG ou WEBP (Proporção recomendada 1.91:1)
                                                        </p>
                                                    </Dragger>
                                                </ImgCrop>
                                            )}
                                        </div>
                                    )}
                                />
                            </Form.Item>
                        </Card>
                        <Card
                            title={
                                <Space size={8}>
                                    <MessageOutlined style={{ color: '#1677ff' }} />
                                    <span>Corpo da Mensagem</span>
                                </Space>
                            }
                            style={{
                                background: cardBg,
                                borderColor: cardBorder,
                                borderRadius: 12,
                            }}
                            headStyle={{
                                borderBottom: `1px solid ${cardBorder}`,
                                fontWeight: 600,
                            }}
                        >
                            <Form.Item
                                required
                                validateStatus={errors.body ? 'error' : 'success'}
                                help={errors.body?.message}
                                style={{ marginBottom: 0 }}
                            >
                                <div
                                    style={{
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        border: `1px solid ${errors.body?.message ? '#ff4d4f' : isDark ? '#303030' : '#d9d9d9'}`,
                                    }}
                                >
                                    <Editor
                                        localization={{
                                            translations: {
                                                'generic.add': 'Adicionar',
                                                'generic.cancel': 'Cancelar',
                                                'components.controls.colorpicker.colorpicker': 'Cor do Texto',
                                                'components.controls.colorpicker.text': 'Texto',
                                                'components.controls.colorpicker.background': 'Fundo',
                                                'components.controls.emoji.emoji': 'Emoji',
                                                'components.controls.fontfamily.fontfamily': 'Fonte',
                                                'components.controls.fontsize.fontsize': 'Tamanho da fonte',
                                                'components.controls.history.history': 'Histórico',
                                                'components.controls.history.undo': 'Desfazer',
                                                'components.controls.history.redo': 'Refazer',
                                                'components.controls.inline.bold': 'Negrito',
                                                'components.controls.inline.italic': 'Itálico',
                                                'components.controls.link.linkTitle': 'Título do Link',
                                                'components.controls.link.linkTarget': 'URL do Link',
                                                'components.controls.link.linkTargetOption': 'Abrir link em nova janela',
                                                'components.controls.link.link': 'Link',
                                                'components.controls.link.unlink': 'Remover Link',
                                                'components.controls.list.list': 'Lista',
                                                'components.controls.list.unordered': 'Marcadores',
                                                'components.controls.list.ordered': 'Numerada',
                                                'components.controls.textalign.textalign': 'Alinhamento',
                                                'components.controls.textalign.left': 'Esquerda',
                                                'components.controls.textalign.center': 'Centro',
                                                'components.controls.textalign.right': 'Direita',
                                                'components.controls.textalign.justify': 'Justificar',
                                            },
                                        }}
                                        editorState={editorState}
                                        defaultEditorState={defaultEditorState}
                                        toolbarStyle={{
                                            backgroundColor: isDark ? '#1a1a1a' : '#f8fafc',
                                            borderColor: isDark ? '#303030' : '#e2e8f0',
                                            borderBottom: `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                                            margin: 0,
                                            padding: '8px 10px',
                                        }}
                                        wrapperClassName="modern-editor-wrapper"
                                        editorClassName="modern-editor-content"
                                        editorStyle={{
                                            minHeight: 200,
                                            maxHeight: 380,
                                            backgroundColor: isDark ? '#141414' : '#ffffff',
                                            color: isDark ? '#f0f0f0' : '#1e293b',
                                            padding: '12px 16px',
                                        }}
                                        onEditorStateChange={onChangeEditor}
                                        toolbar={{
                                            options: [
                                                'inline',
                                                'fontSize',
                                                'list',
                                                'textAlign',
                                                'colorPicker',
                                                'history',
                                                'emoji',
                                                'link',
                                            ],
                                            inline: {
                                                options: ['bold', 'italic'],
                                            },
                                            list: {
                                                options: ['unordered', 'ordered'],
                                            },
                                            textAlign: {
                                                options: ['left', 'center', 'right', 'justify'],
                                            },
                                        }}
                                    />
                                </div>
                            </Form.Item>
                        </Card>

                        {/* Bottom Actions */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingBottom: 24 }}>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate(`/mensagens${isGeneral ? '?type=general' : ''}`)}
                                disabled={isSubmiting}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    height: 38,
                                    borderRadius: 8,
                                    fontWeight: 500,
                                }}
                            >
                                Voltar
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                htmlType="submit"
                                loading={isSubmiting}
                                disabled={isSubmiting || isLoadingAppUsers}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    height: 38,
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    backgroundColor: isGeneral ? '#8b5cf6' : '#1677ff',
                                    borderColor: isGeneral ? '#8b5cf6' : '#1677ff',
                                }}
                            >
                                Salvar Mensagem
                            </Button>
                        </div>
                    </Col>

                    {/* Right Column: Live Smartphone Preview */}
                    <Col xs={24} lg={11} xl={10}>
                        <div style={{ position: 'sticky', top: 20 }}>

                            <PhoneMockup
                                theme={theme}
                                title={title}
                                content={content}
                                body={body}
                                imageUrl={imageUrl}
                                message={message}
                                loggedInUserName={loggedInUserName}
                            />
                        </div>
                    </Col>
                </Row>
            </Form>

            {isViewAppUsersModalVisible && (
                <AppUsersModal onClose={handleCloseAppUsersModal} />
            )}
        </div>
    );
}
